import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { DatePicker, TimePicker } from "../../../../components/shared/DatePicker";
import WeekDaysSelect from "../../../../components/shared/WeekDaysSelect";
import { useMessageResources, useTextResources } from "../../../../language";
import PropTypes from "prop-types";
import { onWarning } from "../../../../components/shared/messages";
import { getWeekDaysOfRange } from "../../../../components/shared/utils";
import { useDateTimeRange } from "../../../../hooks/useDateTimeRange";
import { timeFormat } from "../../../../components/shared/constants";
import moment from "moment";

export const DateRangeCreationPopup = ({ open, onClose, onAdd, RateId }) => {
    const text = useTextResources();
    const message = useMessageResources();
    const {
        startDate,
        endDate,
        startTime,
        endTime,
        setStartDate,
        setEndDate,
        setStartTime,
        setEndTime,
    } = useDateTimeRange({});
    const [weekDays, setWeekDays] = useState([]);

    useEffect(() => {
        const now = moment();
        const roundedTime = now.minutes(Math.ceil(now.minutes() / 15) * 15).seconds(0).milliseconds(0);
        const defaultTime = roundedTime.format(timeFormat);
        setStartDate(moment().startOf("day").toDate());
        setEndDate(moment().startOf("day").toDate());
        setStartTime(defaultTime);
        setEndTime(defaultTime);
        setWeekDays([]);
    }, [open, setEndDate, setEndTime, setStartDate, setStartTime]);

    const handleWeekDaysChange = useCallback((newWeekDays) => {
        setWeekDays(newWeekDays);
    }, []);

    const validate = useCallback(() => {
        return startDate && endDate && weekDays.length > 0 && startTime && endTime;
    }, [startDate, endDate, startTime, endTime, weekDays]);

    const validateSameTime = useCallback(() => {
        return (moment(startTime, 'HH:mm').format('HH:mm') >= moment(endTime, 'HH:mm').format('HH:mm')) &&
                moment(startDate).format('MM DD yyyy') === moment(endDate).format('MM DD yyyy');
    }, [startDate, endDate, startTime, endTime]);

    const onSave = useCallback(() => {
        if (validate() && !validateSameTime())
            onAdd({ StartDate: startDate, EndDate: endDate, Weekdays: weekDays, StartTime: startTime, EndTime: endTime, IsActive: true, RateId });
        else if (!validate) onWarning(message("AllFieldsAreRequired"))
        else onWarning(message("ToDateCannotLessFromDate"))
    }, [validate, onAdd, startDate, endDate, weekDays, startTime, endTime, RateId, message]);

    const initDays = useMemo(() => getWeekDaysOfRange(startDate, endDate), [startDate, endDate]);
    const memoDatesForWeekComponent = useMemo(() => startDate && endDate ? [{ startDate, endDate }] : [], [startDate, endDate]);

    return (
        <Modal size="tiny" open={open} onClose={onClose} dimmer centered>
            <Modal.Header>{text("AddDateRange")}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Group widths={2}>
                        <DatePicker label={text("From")} onChange={setStartDate} clearable={false} value={startDate} minDate={moment().startOf("day").toDate()} />
                        <DatePicker label={text("To")} onChange={setEndDate} clearable={false} value={endDate} minDate={moment().startOf("day").toDate()} />
                    </Form.Group>
                    <Form.Group widths={2}>
                        <Form.Field label={text("From_time")} control={TimePicker} onChange={setStartTime} value={startTime} />
                        <Form.Field label={text("To_time")} control={TimePicker} onChange={setEndTime} value={endTime} />
                    </Form.Group>
                    <WeekDaysSelect onChange={handleWeekDaysChange} initDays={initDays} dates={memoDatesForWeekComponent} />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button negative onClick={onClose}>
                    {text("Close")}
                </Button>
                <Button positive onClick={onSave}>
                    {text("Add")}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

DateRangeCreationPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onAdd: PropTypes.func,
    RateId: PropTypes.number
};
